/**
 * Hook für Online/Offline Status und Sync-State
 */

import { useEffect, useState, useCallback } from 'react';
import { getSyncQueue } from '../lib/syncQueue';

export interface OnlineStatusState {
  isOnline: boolean;
  pendingCount: number;
  readyForSyncCount: number;
  isSyncing: boolean;
  conflictCount: number;
}

/**
 * Hook zum Tracken des Online/Offline Status und Sync-Queue Stats
 * Schöner als navigator.onLine allein, da auch Sync-Status included ist
 */
export function useOnlineStatus(): OnlineStatusState {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [stats, setStats] = useState(() => getSyncQueue().getStats());

  // Event listener für Online/Offline
  useEffect(() => {
    const handleOnline = () => {
      console.log('[useOnlineStatus] Online');
      setIsOnline(true);
      updateStats();
    };

    const handleOffline = () => {
      console.log('[useOnlineStatus] Offline');
      setIsOnline(false);
      updateStats();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Regelmäßig Stats aktualisieren (für UI Refresh)
  useEffect(() => {
    const interval = setInterval(() => {
      updateStats();
    }, 1000); // Jede Sekunde aktualisieren für schnelle UI-Response

    return () => clearInterval(interval);
  }, []);

  const updateStats = useCallback(() => {
    const queue = getSyncQueue();
    setStats(queue.getStats());
  }, []);

  return {
    isOnline,
    pendingCount: stats.totalPending,
    readyForSyncCount: stats.readyForSync,
    // Aus dem Sync-Lock abgeleitet (1s-Poll oben) statt aus totem lokalem State,
    // der nie gesetzt wurde — sonst war "Syncing…" in der UI immer false.
    isSyncing: stats.syncing,
    conflictCount: stats.conflicted,
  };
}

/**
 * Hook für manuelles Sync-State Management
 * Wird vom Sync-Service verwendet um zu signalisieren dass Sync läuft
 */
export function useSyncingState() {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);

  const startSync = useCallback(() => {
    setIsSyncing(true);
    setSyncError(null);
  }, []);

  const endSync = useCallback(() => {
    setIsSyncing(false);
    setLastSyncTime(Date.now());
  }, []);

  const setError = useCallback((error: string | null) => {
    setSyncError(error);
  }, []);

  return {
    isSyncing,
    syncError,
    lastSyncTime,
    startSync,
    endSync,
    setError,
  };
}
